import React from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { NotesBulletList } from './notes-bullet-list';

interface IWhatsAppMessage {
    title: string;
    notesTitle: string;
    addLabel: string;
    notes: string[];
    onNotesChange: (items: string[]) => void;
    // The exact text the copy button puts on the clipboard. Passed in already
    // built rather than assembled here: the page owns the wording, and a preview
    // that regenerated the message itself would be a second implementation of it
    // — which is a preview that can lie.
    preview: string;
    copyLabel: string;
    onCopy: () => void;
    copyDisabled: boolean;
    canEdit: boolean;
}

// One outgoing WhatsApp message: the notes that feed it, what it will say, and
// the button that copies it.
//
// The preview exists because the message is the actual deliverable of this page
// — the plan is only ever read by the crew as this text — and until now the only
// way to see it was to paste it into WhatsApp and look.
export function WhatsAppMessage({
    title,
    notesTitle,
    addLabel,
    notes,
    onNotesChange,
    preview,
    copyLabel,
    onCopy,
    copyDisabled,
    canEdit,
}: IWhatsAppMessage) {
    return (
        <Box sx={{ flex: '1 1 380px', minWidth: 300 }}>
            <Typography variant={'subtitle1'} sx={{ mb: 1 }}>
                {title}
            </Typography>

            <Typography
                variant={'subtitle2'}
                color={'text.secondary'}
                sx={{ mb: 0.5 }}
            >
                {notesTitle}
            </Typography>
            <NotesBulletList
                items={notes}
                onChange={onNotesChange}
                canEdit={canEdit}
                addLabel={addLabel}
            />

            <Typography
                variant={'subtitle2'}
                color={'text.secondary'}
                sx={{ mt: 2, mb: 0.5 }}
            >
                Así se va a enviar
            </Typography>
            <Paper
                variant={'outlined'}
                sx={{
                    px: 1.5,
                    py: 1.5,
                    bgcolor: 'action.hover',
                    // The message is whitespace-significant — blank lines
                    // separate its sections and each machine is its own line —
                    // so it is rendered pre-wrapped rather than as prose.
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    // Long product descriptions would otherwise push the
                    // preview wider than its column.
                    maxHeight: 420,
                    overflow: 'auto',
                }}
            >
                <Typography
                    variant={'body2'}
                    component={'div'}
                    color={preview ? 'text.primary' : 'text.secondary'}
                >
                    {preview || 'Nada que enviar todavía'}
                </Typography>
            </Paper>

            <Button
                variant={'outlined'}
                startIcon={<ContentCopyIcon />}
                onClick={onCopy}
                disabled={copyDisabled}
                sx={{ mt: 1 }}
            >
                {copyLabel}
            </Button>
        </Box>
    );
}
